import { useState } from "react";
import {
  accountService,
  homeService,
  subscriptionService,
} from "@miplatform/api";
import {
  HTTP_STATUSES,
  INVOICE_STATUSES,
  MAX_PAGE_SIZE,
  PRINT_ORIENTATION,
} from "@miplatform/constants";
import { useComponentState, useDebounce, usePrint } from "@miplatform/hooks";
import { AccountType, IInvoiceResponse } from "@miplatform/types";
import { buildSearchQuery } from "@miplatform/utils";
import { useSession } from "@mibuildweb/hooks";
import { OPEN_INVOICES_TAB } from "./constants";

interface GetAllInvoicesResponse {
  status: number;
  data?: IInvoiceResponse[];
  [key: string]: any;
}

export const useAccountsReceivableViewModel = ({ tab }: { tab: string }) => {
  const [invoices, setInvoices] = useState<IInvoiceResponse[]>([]);

  const [tenant, setTenant] = useState<any>(null);
  const [customers, setCustomers] = useState<
    { label: string; value: string }[]
  >([]);
  const [communities, setCommunities] = useState<
    { label: string; value: string }[]
  >([]);

  const [search, setSearch] = useState<string>("");

  const debouncedSearch = useDebounce(search, 500);
  const { handlePrintCustomJsx } = usePrint(tenant);

  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const {
    session: { tenantId },
  } = useSession();

  const getCustomers = async () => {
    return stateManager.run(async () => {
      const { status, data } = await accountService.accounts.getAll({
        sort: "name",
        top: MAX_PAGE_SIZE,
        filters: {
          accountType: {
            in: [AccountType.Builder, AccountType.Service],
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        setCustomers(
          data.map((customer: { name: string; id: string }) => ({
            label: customer.name,
            value: customer.id,
          })) || []
        );
      }
      return [];
    });
  };

  const getCommunities = async () => {
    await stateManager.run(async () => {
      const { status, data } = await homeService.communities.getAll({
        sort: "name",
      });

      if (status === HTTP_STATUSES.OK) {
        setCommunities(
          data.map((community: { name: string; id: string }) => ({
            label: community.name,
            value: community.id,
          })) || []
        );
      }
    });
  };

  const getTenant = async () => {
    await stateManager.run(async () => {
      const response = await subscriptionService.subscriptions.get(tenantId);

      if (response.status === HTTP_STATUSES.OK) {
        setTenant(response.data);
      }
    });
  };

  const getInvoices = ({
    top,
    skip,
    search,
    filters,
  }: {
    top?: number;
    skip?: number;
    search?: string;
    filters?: any;
  }): Promise<GetAllInvoicesResponse | null> => {
    return stateManager.run(async () => {
      const searchQuery = buildSearchQuery(search, [
        "invoiceNumber",
        "home.address.addressLine1",
      ]);

      const filterObj: any = {
        status: {
          eq:
            tab === OPEN_INVOICES_TAB
              ? INVOICE_STATUSES.INVOICED
              : INVOICE_STATUSES.PAID,
        },
        ...searchQuery,
      };

      if (filters) {
        if (filters.accountId) {
          filterObj.billToAccountId = {
            type: "guid",
            value: filters.accountId,
          };
        }
        if (filters.communityId) {
          filterObj["home/communityId"] = {
            type: "guid",
            value: filters.communityId,
          };
        }
        if (filters.syncType && filters.syncType !== "All") {
          filterObj.quickBooksType = { eq: filters.syncType };
        }
        if (filters.syncStatus && filters.syncStatus !== "All") {
          filterObj.syncStatus = { eq: filters.syncStatus };
        }
      }

      const response: GetAllInvoicesResponse =
        await accountService.invoices.getAll({
          filters: filterObj,
          top,
          skip,
          sort: filters?.sortBy ? filters.sortBy : "createdOn",
          enableCaching: false,
        });

      return response.status === HTTP_STATUSES.OK ? response : [];
    });
  };

  const handlePrint = (jsx: React.ReactNode | null) => {
    handlePrintCustomJsx(jsx, {
      orientation: PRINT_ORIENTATION.LANDSCAPE,
    });
  };

  return {
    isLoading,
    stateManager,
    search,
    invoices,
    setInvoices,
    getInvoices,
    debouncedSearch,
    setSearch,
    customers,
    communities,
    getCustomers,
    getCommunities,
    getTenant,
    handlePrint,
  };
};
